import React, { createContext, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Dates } from 'context/DateProvider/DateProvider';
import { Filters } from 'context/FilterProvider/types/Filters';
import { combineUniqueUrlSearchParams } from 'context/SearchParamsProvider/utils/combineUniqueUrlSearchParams';
import {
  toDates,
  toDatesSearchParams,
  toFilters,
  toFiltersSearchParams,
  toSearchTerm,
  toSearchTermSearchParams,
} from 'utils/searchParamsConverter';

const SearchParamsContext = createContext<{
  searchParams: URLSearchParams;
  updateFiltersSearchParams: (newFilters: Filters) => void;
  updateDatesSearchParams: (dates: Dates) => void;
}>({
  searchParams: new URLSearchParams(),
  updateFiltersSearchParams: () => {},
  updateDatesSearchParams: () => {},
});

export const useSearchParamsProvider = () => useContext(SearchParamsContext);

type SearchParamsProviderProps = {
  children: React.ReactNode;
};

const getFiltersSearchParams = (currentParams: URLSearchParams, newFilters?: Filters) => {
  const existingFilters = toFilters(currentParams);
  if (newFilters) {
    return toFiltersSearchParams({ ...existingFilters, ...newFilters });
  } else {
    return toFiltersSearchParams(existingFilters);
  }
};

const getDatesSearchParams = (currentParams: URLSearchParams, newDates?: Dates) => {
  const existingDates = toDates(currentParams);
  if (newDates) {
    return toDatesSearchParams({ ...existingDates, ...newDates });
  } else {
    return toDatesSearchParams(existingDates);
  }
};

const getSearchTermSearchParams = (currentParams: URLSearchParams) => {
  const existingSearchTerm = toSearchTerm(currentParams);
  if (existingSearchTerm) {
    return toSearchTermSearchParams(existingSearchTerm);
  }
  return new URLSearchParams();
};

export const SearchParamsProvider = ({ children }: SearchParamsProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateFiltersSearchParams = (newFilters: Filters) => {
    updateSearchParams(newFilters, undefined);
  };
  const updateDatesSearchParams = (newDates: Dates) => {
    updateSearchParams(undefined, newDates);
  };

  const updateSearchParams = (newFilters?: Filters, newDates?: Dates) => {
    const currentParams = new URLSearchParams(searchParams.toString());
    const filtersQueryParam = getFiltersSearchParams(currentParams, newFilters);
    const datesQueryParam = getDatesSearchParams(currentParams, newDates);
    const searchTermParam = getSearchTermSearchParams(currentParams);
    const urlSearchParams = combineUniqueUrlSearchParams(
      filtersQueryParam,
      datesQueryParam,
      searchTermParam
    );
    setSearchParams(urlSearchParams);
  };

  return (
    <SearchParamsContext.Provider
      value={{ searchParams, updateFiltersSearchParams, updateDatesSearchParams }}
    >
      {children}
    </SearchParamsContext.Provider>
  );
};
